import { useEffect, useMemo, useState, type ReactNode } from "react";
import { Link, useLocation, useNavigate, useParams } from "react-router-dom";
import { useAuth } from "../auth/AuthContext";
import { useLocale } from "../../shared/i18n/LocaleContext";
import {
  LocalizedDateInput,
  LocalizedDateTimeInput,
  LocalizedDecimalInput,
} from "../../shared/i18n/LocalizedInputs";
import { AppModal } from "../../shared/components/AppModal";
import { useAppConfirm } from "../../shared/components/AppConfirmDialog";
import { PageLoading } from "../../shared/components/PageLoading";
import { SortableTableHeader, useSortedRows } from "../../shared/components/SortableTableHeader";
import { getArticleMaterials, getMaterialDeletionAvailability, materialCommand, peekArticleMaterials } from "./materialsApi";
import { ArticleEditorForm } from "./ArticleEditorForm";
import { useIntegrationProtection } from "../../shared/integrations/useIntegrationProtection";
import { IntegrationProtectionBoundary } from "../../shared/integrations/IntegrationManagedField";
import type {
  ArticleItem,
  InventoryItem,
  InventoryMovementKind,
  InventoryStatus,
  MaterialSnapshot,
} from "./types";

type Action = "movement" | "transfer" | "status" | "reservation";
type ArticleInventoryTab = "warehouses" | "lots" | "locations" | "serials";
type FormState = {
  warehouseId: string;
  locationId: string;
  status: InventoryStatus;
  targetWarehouseId: string;
  targetLocationId: string;
  targetStatus: InventoryStatus;
  lotNumber: string;
  serialNumber: string;
  revision: string;
  expiryDate: string;
  kind: InventoryMovementKind;
  reservationKind: "Hard" | "Soft";
  quantity: number;
  reference: string;
  occurredAt: string;
};

export function ArticleInventoryPage() {
  const { articleId, articleWarehouseId } = useParams();
  const navigate = useNavigate();
  const location = useLocation();
  const { user } = useAuth();
  const { t, formatNumber, formatDate, locale } = useLocale();
  const { confirm, confirmationDialog } = useAppConfirm();
  const articleProtection = useIntegrationProtection("Material");
  const movementProtection = useIntegrationProtection("InventoryMovement");
  const warehouseProtection = useIntegrationProtection("Warehouse");
  const lotProtection = useIntegrationProtection("LotSerial");
  const serialProtection = useIntegrationProtection("SerialNumber");
  const [data, setData] = useState<MaterialSnapshot | null>(() => articleId ? peekArticleMaterials(articleId, user?.activeCompanyId) : null);
  const [error, setError] = useState<string | null>(null);
  const [message, setMessage] = useState<string | null>(null);
  const [warehousePage, setWarehousePage] = useState(1);
  const [lotPage, setLotPage] = useState(1);
  const [locationPage, setLocationPage] = useState(1);
  const [serialPage, setSerialPage] = useState(1);
  const [inventoryTab, setInventoryTab] = useState<ArticleInventoryTab>("warehouses");
  const [stockAction, setStockAction] = useState<Action | null>(null);
  const load = async () => {
    try {
      if (articleId) setData(await getArticleMaterials(articleId, user?.activeCompanyId));
      setError(null);
    } catch (e) {
      setError(e instanceof Error ? e.message : "materials.loadError");
    }
  };
  useEffect(() => {
    void load();
  }, [user?.activeCompanyId, articleId]);
  useEffect(() => {
    setWarehousePage(1);
    setLotPage(1);
    setLocationPage(1);
    setSerialPage(1);
  }, [articleId]);
  const article = data?.articles.find((x) => x.id === articleId);
  useEffect(() => {
    if (!article) return;
    const available: ArticleInventoryTab[] = [
      ...(article.inventoryTracked ? ["warehouses" as const] : []),
      ...(article.lotTracked ? ["lots" as const] : []),
      ...(article.inventoryTracked ? ["locations" as const] : []),
      ...(article.serialTracked ? ["serials" as const] : []),
    ];
    if (available.length && !available.includes(inventoryTab)) setInventoryTab(available[0]);
  }, [article, inventoryTab]);
  const routeState = location.state as { returnTo?: string } | null;
  const returnTo = routeState?.returnTo?.startsWith("/app/materiales/")
    ? routeState.returnTo : "/app/materiales/articulos";
  const articleWarehouses = useMemo(
    () => data?.articleWarehouses.filter((x) => x.articleId === articleId) ?? [],
    [data, articleId],
  );
  const lots = useMemo(
    () => data?.lotSerials.filter((x) => x.articleId === articleId && !!x.lotNumber) ?? [],
    [data, articleId],
  );
  const locationRows = useMemo(() => {
    if (!data || !article) return [];
    const warehouseRelations = new Map(
      data.articleWarehouses.filter((relation) => relation.articleId === article.id)
        .map((relation) => [relation.id, relation]),
    );
    const warehouses = new Map(data.warehouses.map((warehouse) => [warehouse.id, warehouse]));
    const locations = new Map(data.warehouseLocations.map((location) => [location.id, location]));
    const lotStocks = new Map(data.articleWarehouseLots.map((stock) => [stock.id, stock]));
    const lotsById = new Map(data.lotSerials.map((lot) => [lot.id, lot]));
    const rows: Array<{id:string;warehouse:MaterialSnapshot['warehouses'][number];location:MaterialSnapshot['warehouseLocations'][number];lotNumber:string|null;onHandQuantity:number;reservedQuantity:number;availableQuantity:number}> = article.lotTracked
      ? data.articleWarehouseLotLocations.flatMap((stock) => {
          const lotStock = lotStocks.get(stock.articleWarehouseLotId);
          const relation = lotStock ? warehouseRelations.get(lotStock.articleWarehouseId) : undefined;
          const lot = lotStock ? lotsById.get(lotStock.lotSerialId) : undefined;
          const location = locations.get(stock.locationId);
          const warehouse = relation ? warehouses.get(relation.warehouseId) : undefined;
          if (!relation || !lot?.lotNumber || !location || !warehouse) return [];
          return [{ id: stock.id, warehouse, location, lotNumber: lot.lotNumber,
            onHandQuantity: stock.onHandQuantity, reservedQuantity: stock.reservedQuantity,
            availableQuantity: stock.availableQuantity }];
        })
      : data.articleWarehouseLocations.flatMap((stock) => {
          const relation = warehouseRelations.get(stock.articleWarehouseId);
          const location = locations.get(stock.locationId);
          const warehouse = relation ? warehouses.get(relation.warehouseId) : undefined;
          if (!relation || !location || !warehouse) return [];
          return [{ id: stock.id, warehouse, location, lotNumber: null,
            onHandQuantity: stock.onHandQuantity, reservedQuantity: stock.reservedQuantity,
            availableQuantity: stock.availableQuantity }];
        });
    return rows.sort((left, right) => left.warehouse.code.localeCompare(right.warehouse.code)
      || left.location.code.localeCompare(right.location.code)
      || (left.lotNumber ?? "").localeCompare(right.lotNumber ?? ""));
  }, [data, article]);
  const warehouseRows = useMemo(() => (data ? articleWarehouses.map(relation => {
    const warehouse = data.warehouses.find(value => value.id === relation.warehouseId);
    return { relation, warehouse };
  }) : []), [data, articleWarehouses]);
  const lotRows = useMemo(() => (data ? lots.map(lot => {
    const stocks = data.articleWarehouseLots.filter(stock => stock.lotSerialId === lot.id);
    return { lot, onHand: stocks.reduce((sum, stock) => sum + stock.onHandQuantity, 0),
      reserved: stocks.reduce((sum, stock) => sum + stock.reservedQuantity, 0),
      available: stocks.reduce((sum, stock) => sum + stock.availableQuantity, 0) };
  }) : []), [data, lots]);
  const serialRows = useMemo(() => (data && article ? data.serialNumbers.filter(serial =>
    serial.articleId === article.id).map(serial => {const stocks=data.articleWarehouseSerials.filter(stock=>
      stock.serialNumberId===serial.id);return {serial,onHand:stocks.reduce((sum,stock)=>sum+stock.onHandQuantity,0),
      reserved:stocks.reduce((sum,stock)=>sum+stock.reservedQuantity,0),available:stocks.reduce((sum,stock)=>sum+stock.availableQuantity,0)}}) : []), [data, article]);
  const warehouseSort = useSortedRows(warehouseRows, {
    code: row => row.warehouse?.code, name: row => row.warehouse?.name,
    minimum: row => row.relation.minimumStock, reorder: row => row.relation.reorderPoint,
    onHand: row => row.relation.onHandQuantity, reserved: row => row.relation.reservedQuantity,
    available: row => row.relation.availableQuantity,
  }, 'available', 'desc', locale);
  const lotSort = useSortedRows(lotRows, {lot:row=>row.lot.lotNumber,revision:row=>row.lot.revision,
    expiry:row=>row.lot.expiryDate,onHand:row=>row.onHand,reserved:row=>row.reserved,available:row=>row.available}, 'available', 'desc', locale);
  const locationSort = useSortedRows(locationRows, {warehouse:row=>row.warehouse.code,location:row=>row.location.code,
    lot:row=>row.lotNumber,onHand:row=>row.onHandQuantity,reserved:row=>row.reservedQuantity,available:row=>row.availableQuantity}, 'available', 'desc', locale);
  const serialSort = useSortedRows(serialRows, {serial:row=>row.serial.serialNumber,revision:row=>row.serial.revision,
    onHand:row=>row.onHand,reserved:row=>row.reserved,available:row=>row.available}, 'available', 'desc', locale);
  if (!data) return <PageLoading label={t("materials.loading")}
    error={error ? t(error as Parameters<typeof t>[0]) : null}/>;
  if (!article)
    return (
      <main className="page-content">
        <p>{t("materials.notFound")}</p>
        <Link to={returnTo}>← {t("materials.articles")}</Link>
      </main>
    );
  if (articleWarehouseId) {
    const relation = data.articleWarehouses.find(x => x.id === articleWarehouseId && x.articleId === article.id);
    if (!relation) return <main className="page-content"><p>{t("materials.notFound")}</p></main>;
    const warehouse = data.warehouses.find(x => x.id === relation.warehouseId);
    return <main className="page-content"><AppModal title={`${article.code} · ${warehouse?.name ?? "—"}`}
      closeLabel={t("nav.closeMenu")} onClose={() => navigate(`/app/materiales/articulos/${article.id}`)} wide
      saveForm={user?.permissions.materials === "Write"}>
      <ArticleWarehouseForm relation={relation} readOnly={user?.permissions.materials !== "Write"}
        save={async body => { try { setMessage(await materialCommand(`/article-warehouses/${relation.id}`, "PUT", body)); await load(); }
          catch(reason){setError(reason instanceof Error ? reason.message : "materials.actionError")} }}/>
      {message && <p className="form-success">{t(message as Parameters<typeof t>[0])}</p>}
      {error && <p className="form-error">{t(error as Parameters<typeof t>[0])}</p>}
    </AppModal></main>;
  }
  const articleIndex = data.articles.findIndex((item) => item.id === article.id);
  const saveArticle = async (body: unknown) => {
    try {
      setMessage(await materialCommand(`/articles/${article.id}`, "PUT", body));
      await load();
    } catch (reason) {
      setError(reason instanceof Error ? reason.message : "materials.actionError");
    }
  };
  const removeArticle = async () => {
    const path = `/articles/${article.id}`;
    setError(null);
    try { const availability=await getMaterialDeletionAvailability(path);
      if(!availability.allowed){setError(availability.code);return;}
      if(!await confirm(t("deletion.confirm"), undefined, true))return;
      await materialCommand(path, "DELETE"); navigate(returnTo); }
    catch (reason) { setError(reason instanceof Error ? reason.message : "deletion.unavailable"); }
  };
  const saveStockAction = async (path: string, body: unknown) => {
    try {
      setError(null);
      setMessage(await materialCommand(path, "POST", body));
      setStockAction(null);
      await load();
    } catch (reason) {
      setError(reason instanceof Error ? reason.message : "materials.actionError");
    }
  };
  if (stockAction) return <main className="page-content">
    <AppModal title={t(`materials.action.${stockAction}`)} closeLabel={t("nav.closeMenu")}
      onClose={() => { setError(null); setStockAction(null); }} wide
      feedback={error ? t(error as Parameters<typeof t>[0]) : null}>
      <ActionForm action={stockAction} article={article} data={data} source={null} save={saveStockAction} />
    </AppModal>
  </main>;
  const canMoveInventory = user?.permissions.materials === "Write" &&
    data.manualInventoryMovementsAllowed && movementProtection.canCreate && article.inventoryTracked;
  return (
    <main className="page-content">
      <AppModal title={`${article.code} · ${article.name}`} closeLabel={t("nav.closeMenu")}
        onClose={() => navigate(returnTo)} wide
        saveForm={user?.permissions.materials === "Write"}
        onDelete={user?.permissions.materials === "Write" && articleProtection.canDelete ? removeArticle : null}
        onPrevious={articleIndex > 0 ? () => navigate(`/app/materiales/articulos/${data.articles[articleIndex - 1].id}`, {state:routeState}) : null}
        onNext={articleIndex < data.articles.length - 1 ? () => navigate(`/app/materiales/articulos/${data.articles[articleIndex + 1].id}`, {state:routeState}) : null}
        feedback={error ? t(error as Parameters<typeof t>[0]) : null}>
        {message && <p className="form-success">{t(message as Parameters<typeof t>[0])}</p>}
        <fieldset className={user?.permissions.materials !== "Write" ? "readonly-detail" : undefined}
          disabled={user?.permissions.materials !== "Write"}>
          <ArticleEditorForm key={article.id} item={article} data={data} save={saveArticle} />
        </fieldset>
      {(article.inventoryTracked || article.lotTracked || article.serialTracked) &&
        <nav className="administration-tabs inventory-tabs" aria-label={t("materials.inventory") }>
          {article.inventoryTracked && <button type="button" className={inventoryTab === "warehouses" ? "active" : undefined} onClick={() => setInventoryTab("warehouses")}>{t("materials.warehouses")}</button>}
          {article.lotTracked && <button type="button" className={inventoryTab === "lots" ? "active" : undefined} onClick={() => setInventoryTab("lots")}>{t("materials.lots")}</button>}
          {article.inventoryTracked && <button type="button" className={inventoryTab === "locations" ? "active" : undefined} onClick={() => setInventoryTab("locations")}>{t("materials.locations")}</button>}
          {article.serialTracked && <button type="button" className={inventoryTab === "serials" ? "active" : undefined} onClick={() => setInventoryTab("serials")}>{t("materials.serialNumbers")}</button>}
        </nav>}
      {article.inventoryTracked && <div className="inventory-action-bar">
        {data.manualInventoryMovementsAllowed ? canMoveInventory && <>
          <button type="button" className="primary-button" onClick={() => setStockAction("movement")}>{t("materials.action.movement")}</button>
          <button type="button" className="secondary-button" onClick={() => setStockAction("transfer")}>{t("materials.action.transfer")}</button>
          <button type="button" className="secondary-button" onClick={() => setStockAction("status")}>{t("materials.action.status")}</button>
        </> : <p className="panel-message warning">{t("materials.manualInventoryIntegrationManaged")}</p>}
      </div>}
      {article.inventoryTracked && inventoryTab === "warehouses" && <Panel title={t("materials.warehouses")}
        action={user?.permissions.materials === "Write" && warehouseProtection.canCreate ? <Link className="primary-button" to="/app/materiales/almacenes?new=1">+ {t("materials.createWarehouse")}</Link> : null}>
        <table className="data-table administration-table">
          <thead><tr>
            <SortHeaders sort={warehouseSort} columns={[['code',t("aplPanel.code")],['name',t("aplPanel.name")],['minimum',t("materials.minimumStock")],['reorder',t("materials.reorderPoint")],['onHand',t("materials.onHand")],['reserved',t("materials.reserved")],['available',t("materials.available")]]}/>
          </tr></thead>
          <tbody>{warehouseRows.length === 0 && <EmptyRow columns={7}/>} {warehouseSort.sortedRows.slice((warehousePage - 1) * 10, warehousePage * 10).map(({relation:x,warehouse}) => <tr key={x.id}>
            <td><Link className="record-code-link" to={`/app/materiales/articulos/${article.id}/almacenes/${x.id}`}><strong>{warehouse?.code??"—"}</strong></Link></td><td>{warehouse?.name??"—"}</td><td>{formatNumber(x.minimumStock)}</td><td>{formatNumber(x.reorderPoint)}</td><td>{formatNumber(x.onHandQuantity)}</td><td>{formatNumber(x.reservedQuantity)}</td><td><strong>{formatNumber(x.availableQuantity)}</strong></td>
          </tr>)}</tbody>
        </table>
        <FixedPager page={warehousePage} total={articleWarehouses.length} setPage={setWarehousePage}/>
      </Panel>}
      {article.lotTracked && inventoryTab === "lots" && <Panel title={t("materials.lots")}
        action={user?.permissions.materials === "Write" && lotProtection.canCreate ? <Link className="primary-button" state={{returnTo: location.pathname}}
            to={`/app/materiales/lotes/nuevo?articleId=${article.id}`}>
            + {t("materials.createLot")}
          </Link> : null}>
        <table className="data-table administration-table">
          <thead><tr><SortHeaders sort={lotSort} columns={[['lot',t("materials.lot")],['revision',t("materials.revision")],['expiry',t("materials.expiryDate")],['onHand',t("materials.onHand")],['reserved',t("materials.reserved")],['available',t("materials.available")]]}/></tr></thead>
          <tbody>{lotRows.length === 0 && <EmptyRow columns={6}/>} {lotSort.sortedRows.slice((lotPage - 1) * 10, lotPage * 10).map(({lot,onHand,reserved,available}) => {
            return <tr key={lot.id}><td><Link className="record-code-link" state={{returnTo:location.pathname}} to={`/app/materiales/lotes/${lot.id}`}>{lot.lotNumber}</Link></td>
              <td>{lot.revision||"—"}</td><td>{lot.expiryDate?formatDate(lot.expiryDate):"—"}</td>
              <td>{formatNumber(onHand)}</td><td>{formatNumber(reserved)}</td><td><strong>{formatNumber(available)}</strong></td></tr>})}</tbody>
        </table><FixedPager page={lotPage} total={lots.length} setPage={setLotPage}/>
      </Panel>}
      {article.inventoryTracked && inventoryTab === "locations" && <Panel title={t("materials.locations")}>
        <table className="data-table administration-table">
          <thead><tr>
            <SortHeaders sort={locationSort} columns={[['warehouse',t("materials.warehouse")],['location',t("materials.location")],...(article.lotTracked?[["lot",t("materials.lot")] as const]:[]),['onHand',t("materials.onHand")],['reserved',t("materials.reserved")],['available',t("materials.available")]]}/>
          </tr></thead>
          <tbody>
            {locationRows.length === 0 && <EmptyRow columns={5 + Number(article.lotTracked)}/>}
            {locationSort.sortedRows.slice((locationPage - 1) * 10, locationPage * 10).map((row) => <tr key={row.id}>
              <td><strong>{row.warehouse.code}</strong> · {row.warehouse.name}</td>
              <td><strong>{row.location.code}</strong>{row.location.name ? ` · ${row.location.name}` : ""}</td>
              {article.lotTracked && <td>{row.lotNumber}</td>}
              <td>{formatNumber(row.onHandQuantity)}</td><td>{formatNumber(row.reservedQuantity)}</td>
              <td><strong>{formatNumber(row.availableQuantity)}</strong></td>
            </tr>)}
          </tbody>
        </table>
        <FixedPager page={locationPage} total={locationRows.length} setPage={setLocationPage}/>
      </Panel>}
      {article.serialTracked && inventoryTab === "serials" && <Panel title={t("materials.serialNumbers")}
        action={user?.permissions.materials === "Write" && serialProtection.canCreate ? <Link className="primary-button" state={{returnTo:location.pathname}} to={`/app/materiales/numeros-serie/nuevo?articleId=${article.id}`}>+ {t("materials.createSerialNumber")}</Link> : null}>
        <table className="data-table administration-table"><thead><tr><SortHeaders sort={serialSort} columns={[["serial",t("materials.serial")],["revision",t("materials.revision")],["onHand",t("materials.onHand")],["reserved",t("materials.reserved")],["available",t("materials.available")]]}/></tr></thead><tbody>
          {serialRows.length===0&&<EmptyRow columns={5}/>} {serialSort.sortedRows.slice((serialPage-1)*10,serialPage*10).map(({serial,onHand,reserved,available})=><tr key={serial.id}><td><Link className="record-code-link" state={{returnTo:location.pathname}} to={`/app/materiales/numeros-serie/${serial.id}`}>{serial.serialNumber}</Link></td><td>{serial.revision||"—"}</td><td>{formatNumber(onHand)}</td><td>{formatNumber(reserved)}</td><td><strong>{formatNumber(available)}</strong></td></tr>)}
        </tbody></table><FixedPager page={serialPage} total={serialRows.length} setPage={setSerialPage}/>
      </Panel>}
      {article.inventoryTracked && <Panel title={t("materials.movements")}>
        <table className="data-table administration-table"><thead><tr>
          <th>{t("materials.occurredAt")}</th><th>{t("materials.movementKind")}</th>
          <th>{t("materials.warehouse")}</th><th>{t("materials.location")}</th>
          {article.lotTracked && <th>{t("materials.lot")}</th>}
          {article.serialTracked && <th>{t("materials.serial")}</th>}
          <th>{t("materials.quantity")}</th><th>{t("materials.reference")}</th>
        </tr></thead><tbody>
          {data.inventoryMovements.length === 0 && <EmptyRow columns={6 + Number(article.lotTracked) + Number(article.serialTracked)}/>}
          {data.inventoryMovements.map(movement => <tr key={movement.id}>
            <td>{formatDate(movement.occurredAtUtc)}</td>
            <td>{t(`materials.movementKind.${movement.kind}`)}</td>
            <td>{data.warehouses.find(x => x.id === movement.warehouseId)?.code ?? "—"}</td>
            <td>{data.warehouseLocations.find(x => x.id === movement.locationId)?.code ?? "—"}</td>
            {article.lotTracked && <td>{movement.lotNumber || "—"}</td>}
            {article.serialTracked && <td>{movement.serialNumber || "—"}</td>}
            <td>{formatNumber(movement.quantity)}</td><td>{movement.reference || "—"}</td>
          </tr>)}
        </tbody></table>
      </Panel>}
      </AppModal>
      {confirmationDialog}
    </main>
  );
}

function SortHeaders<K extends string>({sort,columns}:{sort:{sortKey:K;sortDirection:'asc'|'desc';toggle:(key:K)=>void};columns:ReadonlyArray<readonly [K,string]>}){
  return <>{columns.map(([key,label])=><SortableTableHeader key={key} sortKey={key} activeKey={sort.sortKey} direction={sort.sortDirection} label={label} onSort={sort.toggle}/>)}</>
}

function ArticleWarehouseForm({relation,readOnly,save}:{relation:MaterialSnapshot["articleWarehouses"][number];readOnly:boolean;save:(body:unknown)=>void}){
  const {t,locale}=useLocale(); const [f,setF]=useState({minimumStock:relation.minimumStock,reorderPoint:relation.reorderPoint,minimumOrderQuantity:relation.minimumOrderQuantity,orderMultiple:relation.orderMultiple,maximumStock:relation.maximumStock??0});
  const field=(key:keyof typeof f)=><LocalizedDecimalInput locale={locale} min={0} value={f[key]} onChange={value=>setF({...f,[key]:value})}/>;
  return <IntegrationProtectionBoundary entity="ArticleWarehouse" fields={['minimumStock','reorderPoint','minimumOrderQuantity','orderMultiple','maximumStock',null,null,null]}><form className="administration-form record-form article-warehouse-form" onSubmit={event=>{event.preventDefault();save({...f,maximumStock:f.maximumStock||null})}}>
    <fieldset disabled={readOnly} className={readOnly?"readonly-detail article-warehouse-fieldset":"article-warehouse-fieldset"}>
      <section className="record-detail-panel article-warehouse-section">
        <div className="compact-form-grid">
          <Field label={t("materials.minimumStock")}>{field("minimumStock")}</Field>
          <Field label={t("materials.reorderPoint")}>{field("reorderPoint")}</Field>
          <Field label={t("materials.minimumQuantity")}>{field("minimumOrderQuantity")}</Field>
          <Field label={t("materials.orderMultiple")}>{field("orderMultiple")}</Field>
          <Field label={t("materials.maximumQuantity")}>{field("maximumStock")}</Field>
        </div>
      </section>
      <section className="record-detail-panel article-warehouse-section article-warehouse-stock-section">
        <h3>{t("materials.inventory")}</h3>
        <div className="compact-form-grid three-columns">
          <Field label={t("materials.onHand")}><input disabled value={relation.onHandQuantity}/></Field>
          <Field label={t("materials.reserved")}><input disabled value={relation.reservedQuantity}/></Field>
          <Field label={t("materials.available")}><input disabled value={relation.availableQuantity}/></Field>
        </div>
      </section>
      {!readOnly&&<div className="form-actions"><button className="primary-button" type="submit">{t("administration.save")}</button></div>}
    </fieldset>
  </form></IntegrationProtectionBoundary>;
}

export function ActionForm({
  action,
  article,
  data,
  source,
  save,
}: {
  action: Action;
  article: ArticleItem;
  data: MaterialSnapshot;
  source: InventoryItem | null;
  save: (path: string, body: unknown) => void;
}) {
  const { t, locale } = useLocale();
  const warehouses = data.warehouses.filter((x) => x.isActive);
  const [f, setF] = useState<FormState>({
    warehouseId: source?.warehouseId ?? warehouses[0]?.id ?? "",
    locationId: source?.locationId ?? "",
    status: source?.status ?? "Available",
    targetWarehouseId: "",
    targetLocationId: "",
    targetStatus: "Blocked",
    lotNumber: source?.lotNumber ?? "",
    serialNumber: source?.serialNumber ?? "",
    revision: source?.revision ?? article.revision,
    expiryDate: source?.expiryDate ?? "",
    kind: "Receipt",
    reservationKind: "Hard",
    quantity: 1,
    reference: "",
    occurredAt: new Date().toISOString().slice(0, 16),
  });
  const locations = data.warehouseLocations.filter(
    (x) => x.warehouseId === f.warehouseId && x.isActive,
  );
  const targets = data.warehouseLocations.filter(
    (x) => x.warehouseId === f.targetWarehouseId && x.isActive,
  );
  const sourceWarehouse = warehouses.find(x => x.id === f.warehouseId);
  const targetWarehouse = warehouses.find(x => x.id === f.targetWarehouseId);
  const outgoing = action !== "movement" || ["Consumption", "AdjustmentDecrease"].includes(f.kind);
  const matchingPositions = data.inventory.filter(position =>
    position.articleId === article.id && position.warehouseId === f.warehouseId &&
    position.status === f.status && position.lotNumber === f.lotNumber &&
    position.serialNumber === f.serialNumber && position.revision === f.revision &&
    position.availableQuantity > 0);
  const availableLocationIds = new Set(matchingPositions.map(position => position.locationId).filter(Boolean));
  const sourceLocations = outgoing
    ? locations.filter(location => availableLocationIds.has(location.id)) : locations;
  useEffect(() => {
    if (!outgoing || !sourceWarehouse?.managesLocations || f.locationId || sourceLocations.length !== 1) return;
    setF(current => ({ ...current, locationId: sourceLocations[0].id }));
  }, [outgoing, sourceWarehouse?.managesLocations, f.locationId, sourceLocations]);
  const submit = () => {
    const common = {
      articleId: article.id,
      unitId: article.baseUnitId,
      lotNumber: f.lotNumber,
      serialNumber: f.serialNumber,
      revision: f.revision,
      expiryDate: f.expiryDate || null,
      quantity: f.quantity,
      reference: f.reference,
      idempotencyKey: crypto.randomUUID(),
    };
    const occurredAtUtc = new Date(f.occurredAt).toISOString();
    if (action === "movement")
      save("/inventory-movements", {
        ...common,
        warehouseId: f.warehouseId,
        locationId: f.locationId || null,
        status: f.status,
        kind: f.kind,
        occurredAtUtc,
      });
    if (action === "transfer")
      save("/inventory-transfers", {
        ...common,
        sourceWarehouseId: f.warehouseId,
        sourceLocationId: f.locationId || null,
        targetWarehouseId: f.targetWarehouseId,
        targetLocationId: f.targetLocationId || null,
        status: f.status,
        occurredAtUtc,
      });
    if (action === "status")
      save("/inventory-status-changes", {
        ...common,
        warehouseId: f.warehouseId,
        locationId: f.locationId || null,
        sourceStatus: f.status,
        targetStatus: f.targetStatus,
        occurredAtUtc,
      });
    if (action === "reservation")
      save("/inventory-reservations", {
        ...common,
        warehouseId: f.warehouseId,
        locationId: f.locationId || null,
        inventoryStatus: f.status,
        kind: f.reservationKind,
      });
  };
  return (
    <form
      className="administration-form modal-form"
      onSubmit={(e) => {
        e.preventDefault();
        submit();
      }}
    >
      <div className="compact-form-grid">
        <Field label={t("materials.warehouse")}>
          <select
            required
            value={f.warehouseId}
            disabled={!!source}
            onChange={(e) =>
              setF({ ...f, warehouseId: e.target.value, locationId: "" })
            }
          >
            <option value="">—</option>
            {warehouses.map((x) => (
              <option key={x.id} value={x.id}>
                {x.code} · {x.name}
              </option>
            ))}
          </select>
        </Field>
        <Field label={t("materials.location")}>
          <select
            required={sourceWarehouse?.managesLocations}
            value={f.locationId}
            disabled={!!source}
            onChange={(e) => setF({ ...f, locationId: e.target.value })}
          >
            <option value="">—</option>
            {sourceLocations.map((x) => (
              <option key={x.id} value={x.id}>
                {x.code} · {x.name}
              </option>
            ))}
          </select>
        </Field>
        {action === "movement" && (
          <Field label={t("materials.movementKind")}>
            <select
              value={f.kind}
              onChange={(e) =>
                setF({ ...f, kind: e.target.value as InventoryMovementKind })
              }
            >
              {[
                "Receipt",
                "Consumption",
                "Production",
                "AdjustmentIncrease",
                "AdjustmentDecrease",
                "Return",
              ].map((x) => (
                <option key={x} value={x}>
                  {t(`materials.movementKind.${x}` as Parameters<typeof t>[0])}
                </option>
              ))}
            </select>
          </Field>
        )}
        {action === "transfer" && (
          <>
            <Field label={t("materials.targetWarehouse")}>
              <select
                required
                value={f.targetWarehouseId}
                onChange={(e) =>
                  setF({
                    ...f,
                    targetWarehouseId: e.target.value,
                    targetLocationId: "",
                  })
                }
              >
                <option value="">—</option>
                {warehouses.map((x) => (
                  <option key={x.id} value={x.id}>
                    {x.code} · {x.name}
                  </option>
                ))}
              </select>
            </Field>
            <Field label={t("materials.targetLocation")}>
              <select
                required={targetWarehouse?.managesLocations}
                value={f.targetLocationId}
                onChange={(e) =>
                  setF({ ...f, targetLocationId: e.target.value })
                }
              >
                <option value="">—</option>
                {targets.map((x) => (
                  <option key={x.id} value={x.id}>
                    {x.code} · {x.name}
                  </option>
                ))}
              </select>
            </Field>
          </>
        )}
        {action === "status" && (
          <EnumField
            label={t("materials.targetStatus")}
            value={f.targetStatus}
            values={["Available", "Blocked", "Quarantine", "QualityInspection"]}
            prefix="materials.inventoryStatus"
            change={(value) =>
              setF({ ...f, targetStatus: value as InventoryStatus })
            }
          />
        )}{" "}
        {action === "reservation" && (
          <EnumField
            label={t("materials.reservationKind")}
            value={f.reservationKind}
            values={["Hard", "Soft"]}
            prefix="materials.reservationKind"
            change={(value) =>
              setF({ ...f, reservationKind: value as "Hard" | "Soft" })
            }
          />
        )}
        <EnumField
          label={t("materials.status")}
          value={f.status}
          disabled={!!source}
          values={["Available", "Blocked", "Quarantine", "QualityInspection"]}
          prefix="materials.inventoryStatus"
          change={(value) => setF({ ...f, status: value as InventoryStatus })}
        />
        <Field label={t("materials.quantity")}>
          <LocalizedDecimalInput
            locale={locale}
            min={0.000001}
            max={article.serialTracked ? 1 : undefined}
            disabled={article.serialTracked}
            value={f.quantity}
            onChange={(quantity) => setF({ ...f, quantity })}
          />
        </Field>
        <Field label={t("materials.reference")}>
          <input
            maxLength={120}
            value={f.reference}
            onChange={(e) => setF({ ...f, reference: e.target.value })}
          />
        </Field>
        <TraceFields article={article} data={data} f={f} setF={setF} />
        {action === "movement" && (
          <Field label={t("materials.occurredAt")}>
            <LocalizedDateTimeInput
              required
              locale={locale}
              value={f.occurredAt}
              dateAriaLabel={t("materials.occurredAt")}
              timeAriaLabel={t("input.selectTime")}
              onChange={(occurredAt) => setF({ ...f, occurredAt })}
            />
          </Field>
        )}
      </div>
      <div className="form-actions">
        <button
          className="primary-button"
          disabled={!article.baseUnitId}
          type="submit"
        >
          {t("administration.save")}
        </button>
      </div>
    </form>
  );
}
function TraceFields({
  article,
  data,
  f,
  setF,
}: {
  article: ArticleItem;
  data: MaterialSnapshot;
  f: FormState;
  setF: (value: FormState) => void;
}) {
  const { t, locale } = useLocale();
  return (
    <>
      {article.lotTracked && <Field label={t("materials.lot")}>
        <select required value={f.lotNumber} onChange={(e) => {
          const lot = data.lotSerials.find(item => item.lotNumber === e.target.value);
          setF({ ...f, lotNumber: e.target.value, revision: lot?.revision ?? "",
            expiryDate: lot?.expiryDate ?? "" });
        }}><option value="">—</option>{data.lotSerials.filter(item => item.articleId === article.id && item.isActive)
          .map(item => <option value={item.lotNumber} key={item.id}>{item.lotNumber}</option>)}</select>
      </Field>}
      {article.serialTracked && <Field label={t("materials.serial")}>
        <select required value={f.serialNumber} onChange={(e) => {
          const serial = data.serialNumbers.find(item => item.serialNumber === e.target.value);
          setF({ ...f, serialNumber: e.target.value, revision: serial?.revision ?? "", quantity: 1 });
        }}><option value="">—</option>{data.serialNumbers.filter(item => item.articleId === article.id && item.isActive)
          .map(item => <option value={item.serialNumber} key={item.id}>{item.serialNumber}</option>)}</select>
      </Field>}
      <Field label={t("materials.revision")}>
        <input
          disabled={article.lotTracked || article.serialTracked}
          maxLength={50}
          value={f.revision}
          onChange={(e) => setF({ ...f, revision: e.target.value })}
        />
      </Field>
      {article.lotTracked && <Field label={t("materials.expiryDate")}>
        <LocalizedDateInput
          disabled
          locale={locale}
          value={f.expiryDate}
          ariaLabel={t("materials.expiryDate")}
          onChange={(expiryDate) => setF({ ...f, expiryDate })}
        />
      </Field>}
    </>
  );
}
function EnumField({
  label,
  value,
  values,
  prefix,
  change,
  disabled,
}: {
  label: string;
  value: string;
  values: string[];
  prefix: string;
  change: (value: string) => void;
  disabled?: boolean;
}) {
  const { t } = useLocale();
  return (
    <Field label={label}>
      <select
        value={value}
        disabled={disabled}
        onChange={(e) => change(e.target.value)}
      >
        {values.map((x) => (
          <option key={x} value={x}>
            {t(`${prefix}.${x}` as Parameters<typeof t>[0])}
          </option>
        ))}
      </select>
    </Field>
  );
}
function Panel({
  title,
  action,
  children,
}: {
  title: string;
  action?: ReactNode;
  children: ReactNode;
}) {
  return (
    <section className="data-panel administration-panel inventory-detail-panel">
      <div className="section-heading">
        <h2>{title}</h2>
        {action}
      </div>
      <div className="administration-table-wrap">{children}</div>
    </section>
  );
}
function EmptyRow({columns}:{columns:number}) {
  const {t}=useLocale();
  return <tr><td className="empty-state" colSpan={columns}>{t("administration.empty")}</td></tr>
}
function FixedPager({page,total,setPage}:{page:number;total:number;setPage:(page:number)=>void}) {
  const {t,formatNumber}=useLocale(); const pages=Math.max(1,Math.ceil(total/10));
  if(total<=10)return null;
  return <footer className="table-pagination inventory-fixed-pagination">
    <span className="record-list-pagination-summary">
      {t("recordList.pageOf").replace("{page}",formatNumber(page)).replace("{total}",formatNumber(pages))}
      {" · "}{t("recordList.totalRecords")}: {formatNumber(total)}
    </span>
    <div className="record-list-pagination-actions">
      <button className="secondary-button" type="button" disabled={page<=1} onClick={()=>setPage(page-1)}>{t("recordList.previousPage")}</button>
      <button className="secondary-button" type="button" disabled={page>=pages} onClick={()=>setPage(page+1)}>{t("recordList.nextPage")}</button>
    </div>
  </footer>
}
function Field({ label, children }: { label: string; children: ReactNode }) {
  return (
    <label>
      <span>{label}</span>
      {children}
    </label>
  );
}
